function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function extractMessage(payload: unknown): string | null {
  if (typeof payload === "string" && payload.trim()) return payload.trim();
  const record = asRecord(payload);
  const nestedError = asRecord(record.error);
  return asString(record.message)
    ?? asString(nestedError.message)
    ?? asString(record.error)
    ?? null;
}

function extractCode(payload: unknown): string | null {
  const record = asRecord(payload);
  const nestedError = asRecord(record.error);
  return asString(nestedError.code)
    ?? asString(record.error)
    ?? null;
}

async function readErrorPayload(error: unknown): Promise<unknown | null> {
  const context = asRecord(error).context;
  if (!context) return null;

  // Supabase normally supplies a Response here, but development proxies and
  // wrapped function errors can already contain the decoded response object.
  // Treat that object as payload instead of later rendering it as [object Object].
  if (typeof (context as { json?: unknown }).json !== "function" &&
    typeof (context as { text?: unknown }).text !== "function") {
    return context;
  }

  let response = context as Response;
  try {
    if (typeof response.clone === "function") response = response.clone();
  } catch {
    response = context as Response;
  }
  try {
    return await response.json();
  } catch {
    try {
      return await (context as Response).text();
    } catch {
      return null;
    }
  }
}

export async function getSupabaseFunctionErrorDetails(
  error: unknown,
  fallback: string,
): Promise<{ code: string | null; message: string; status: number | null }> {
  const payload = await readErrorPayload(error);
  const message = payload === null ? null : extractMessage(payload);
  const code = payload === null ? null : extractCode(payload);
  const status = getSupabaseFunctionHttpStatus(error);

  if (message) return { code, message, status };

  if (error instanceof Error && error.message && error.message !== "Edge Function returned a non-2xx status code") {
    return { code, message: error.message, status };
  }

  return { code, message: fallback, status };
}

/** HTTP status is deliberately separate from the public message: callers use it
 * for recovery policy, never for rendering raw Edge Function errors. */
export function getSupabaseFunctionHttpStatus(error: unknown): number | null {
  const context = asRecord(asRecord(error).context);
  const status = context.status;
  return typeof status === "number" && Number.isInteger(status) ? status : null;
}

export function isSupabaseFunctionIdleTimeoutMessage(message: unknown): boolean {
  return typeof message === "string" &&
    /request idle timeout limit \(\d+s\) reached/i.test(message.trim());
}

export async function getSupabaseFunctionErrorMessage(error: unknown, fallback: string): Promise<string> {
  const { message } = await getSupabaseFunctionErrorDetails(error, fallback);
  return message;
}
